import { EditOutlined } from '@ant-design/icons';
import { Button, Modal, Tooltip, Typography, message } from 'antd';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { SidebarHeader } from '../SidebarHeader';
import { useEntityData } from '../../../../EntityContext';
import { EMPTY_MESSAGES } from '../../../../constants';
import SetDataProductModal from './SetDataProductModal';
import { DataProductLink } from '../../../../../../shared/tags/DataProductLink';
import { useBatchSetDataProductMutation } from '../../../../../../../graphql/dataProduct.generated';
import { useTranslation } from '../../../../../../../i18n/useTranslation';
import { DataProduct } from '../../../../../../../types.generated';

const EmptyText = styled(Typography.Paragraph)`
    &&& {
        border-top: none;
        padding-top: 0;
    }
`;

interface Props {
    readOnly?: boolean;
}

export default function DataProductSection({ readOnly }: Props) {
    const { t } = useTranslation();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const { entityData, urn } = useEntityData();
    // Only disable when explicitly set to true (null and false are treated as enabled)
    const isReadOnly = readOnly || entityData?.readOnly?.readOnly === true;
    const [batchSetDataProductMutation] = useBatchSetDataProductMutation();
    const [dataProduct, setDataProduct] = useState<DataProduct | null>(null);
    const dataProductRelationships = entityData?.dataProduct?.relationships;
    const siblingUrns: string[] = entityData?.siblings?.siblings?.map((sibling) => sibling?.urn || '') || [];

    useEffect(() => {
        if (dataProductRelationships && dataProductRelationships.length > 0) {
            setDataProduct(dataProductRelationships[0].entity as DataProduct);
        }
    }, [dataProductRelationships]);

    function removeDataProduct() {
        batchSetDataProductMutation({ variables: { input: { resourceUrns: [urn, ...siblingUrns] } } })
            .then(() => {
                message.success({ content: t('Removed Data Product.'), duration: 2 });
                setDataProduct(null);
            })
            .catch((e: unknown) => {
                message.destroy();
                if (e instanceof Error) {
                    message.error({
                        content: t('Failed to remove data product. An unknown error occurred.'),
                        duration: 3,
                    });
                }
            });
    }

    const onRemoveDataProduct = () => {
        Modal.confirm({
            title: t('Confirm Data Product Removal'),
            content: t('Are you sure you want to remove this data product?'),
            centered: true,
            onOk() {
                removeDataProduct();
            },
            onCancel() {},
            okText: t('Yes'),
            cancelText: t('Cancel'),
            maskClosable: true,
            closable: true,
        });
    };

    return (
        <div className="pa-4">
            <SidebarHeader title={t('Data Product')} />
            {dataProduct && (
                <DataProductLink
                    dataProduct={dataProduct}
                    closable={!isReadOnly}
                    onClose={(e) => {
                        e.preventDefault();
                        onRemoveDataProduct();
                    }}
                    fontSize={14}
                />
            )}
            {!dataProduct && (
                <>
                    <EmptyText type="secondary">
                        {t(EMPTY_MESSAGES.dataProduct.title)}. {t(EMPTY_MESSAGES.dataProduct.description)}
                    </EmptyText>
                    <Tooltip title={isReadOnly ? t('ASSET_MARKED_AS_READ_ONLY') : undefined}>
                        <Button
                            type="default"
                            onClick={() => setIsModalVisible(true)}
                            aria-label={t('Set Data Product')}
                            disabled={isReadOnly}
                        >
                            <EditOutlined aria-hidden="true" /> {t('Set Data Product')}
                        </Button>
                    </Tooltip>
                </>
            )}
            {isModalVisible && (
                <SetDataProductModal
                    urns={[urn, ...siblingUrns]}
                    currentDataProduct={dataProduct || null}
                    onModalClose={() => setIsModalVisible(false)}
                    setDataProduct={setDataProduct}
                />
            )}
        </div>
    );
}
